import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import KeyboardDiagram, { fingerLabel } from "./KeyboardDiagram";
import useArabicKeyboard from "./useArabicKeyboard";
import {
  accuracyPercent,
  expectedKey,
  isNamedKey,
  keyCapLabel,
  keyName,
  keyToken,
  toClusters,
  toCodePoints,
  wordsPerMinute,
} from "./arabicText";

/**
 * One drill: show the target, judge each keystroke, keep the score.
 *
 * The learner is blocked on a wrong key rather than being allowed to run ahead
 * with mistakes. For a tutor that is the point — the drill is teaching where a
 * key *is*, so it has to insist on that key. Backspace still steps back, so a
 * letter can be retyped deliberately.
 */
export default function TypingDrill({ drill, layout, charIndex, remap, onComplete, onExit }) {
  const points = useMemo(() => toCodePoints(drill.target), [drill.target]);
  const clusters = useMemo(() => toClusters(drill.target), [drill.target]);

  const [cursor, setCursor] = useState(0);
  const [wrongAt, setWrongAt] = useState(null); // flashes the mistyped key red
  const [startedAt, setStartedAt] = useState(null);
  const [finished, setFinished] = useState(false);
  const [tally, setTally] = useState({ correct: 0, total: 0 });
  const [elapsed, setElapsed] = useState(0);

  // Per-key counters, accumulated outside React state — they are written on
  // every keystroke and only ever read at the end.
  const keyStats = useRef({ attempts: {}, errors: {} });
  const submitted = useRef(false);

  // Reset everything when the drill changes.
  useEffect(() => {
    setCursor(0);
    setWrongAt(null);
    setStartedAt(null);
    setFinished(false);
    setTally({ correct: 0, total: 0 });
    setElapsed(0);
    keyStats.current = { attempts: {}, errors: {} };
    submitted.current = false;
  }, [drill.key]);

  // Live timer.
  useEffect(() => {
    if (!startedAt || finished) return undefined;
    const id = setInterval(() => setElapsed(Date.now() - startedAt), 200);
    return () => clearInterval(id);
  }, [startedAt, finished]);

  const nextKey = useMemo(
    () => (finished ? null : expectedKey(points, cursor, charIndex, drill.prefer_ligature)),
    [points, cursor, charIndex, drill.prefer_ligature, finished]
  );

  const handleChar = useCallback(
    (char) => {
      if (finished || cursor >= points.length) return;

      const now = Date.now();
      const started = startedAt ?? now;
      if (startedAt === null) setStartedAt(now);

      const expected = expectedKey(points, cursor, charIndex, drill.prefer_ligature);
      const token = expected ? keyToken(expected.code, expected.shift) : "unknown";
      keyStats.current.attempts[token] = (keyStats.current.attempts[token] || 0) + 1;

      // A keystroke may emit two code points (the lām-alef ligature key), and
      // the same text can be reached either way — so judge the emitted string
      // against the target rather than against one specific key.
      const emitted = Array.from(char);
      const ahead = points.slice(cursor, cursor + emitted.length).join("");

      if (ahead === char) {
        const next = cursor + emitted.length;
        setCursor(next);
        setWrongAt(null);
        setTally((t) => ({ correct: t.correct + 1, total: t.total + 1 }));

        if (next >= points.length) {
          setFinished(true);
          setElapsed(now - started);
        }
        return;
      }

      keyStats.current.errors[token] = (keyStats.current.errors[token] || 0) + 1;
      setWrongAt(expected ? { code: expected.code, shift: expected.shift } : null);
      setTally((t) => ({ ...t, total: t.total + 1 }));
    },
    [finished, cursor, points, charIndex, drill.prefer_ligature, startedAt]
  );

  const handleBackspace = useCallback(() => {
    if (finished) return;
    setWrongAt(null);
    setCursor((c) => Math.max(0, c - 1));
  }, [finished]);

  useArabicKeyboard({
    layout: layout?.keys,
    remap,
    enabled: !finished,
    onChar: handleChar,
    onBackspace: handleBackspace,
  });

  // Report the result once, after the final keystroke.
  useEffect(() => {
    if (!finished || submitted.current) return;
    submitted.current = true;
    onComplete({
      wpm: wordsPerMinute(points.length, elapsed),
      accuracy: accuracyPercent(tally.correct, tally.total),
      elapsedMs: elapsed,
      keystrokes: tally.total,
      mistakes: tally.total - tally.correct,
      keyAttempts: keyStats.current.attempts,
      keyErrors: keyStats.current.errors,
    });
  }, [finished, elapsed, points.length, tally, onComplete]);

  const liveWpm = wordsPerMinute(cursor, elapsed);
  const liveAccuracy = accuracyPercent(tally.correct, tally.total);
  const percent = points.length ? Math.round((cursor / points.length) * 100) : 0;

  return (
    <div className="flex flex-col gap-4">
      {/* ── intro ── */}
      <div>
        <h3 className="font-cormorant font-semibold text-xl" style={{ color: "var(--gold-bright)" }}>
          {drill.title}
        </h3>
        <p className="text-sm mt-1" style={{ color: "var(--parchment-dim)" }}>
          {drill.intro}
        </p>
      </div>

      {/* ── the target text ──
          Every cluster span keeps the same font-family, size and weight. Only
          colour and background vary, because changing a font property would
          split the shaping run and break the joins between Arabic letters. */}
      <div
        dir="rtl"
        lang="ar"
        className="font-arabic rounded-xl px-5 py-6 leading-[2.1]"
        style={{
          background: "var(--ink)",
          border: "1px solid var(--panel-line)",
          fontSize: "1.9rem",
          fontWeight: 400,
        }}
      >
        {clusters.map((cluster, i) => {
          const done = cluster.end <= cursor;
          const current = cluster.start <= cursor && cursor < cluster.end;
          const partial = current && cursor > cluster.start;
          return (
            <span
              key={i}
              style={{
                color: done
                  ? "var(--parchment)"
                  : current
                  ? "var(--gold-bright)"
                  : "var(--parchment-dim)",
                background: current
                  ? wrongAt
                    ? "rgba(220,90,90,0.22)"
                    : "var(--gold-bg)"
                  : "transparent",
                opacity: done || current ? 1 : 0.5,
                borderBottom: partial ? "2px solid var(--gold)" : "2px solid transparent",
              }}
            >
              {cluster.text}
            </span>
          );
        })}
      </div>

      {/* ── progress + live stats ── */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex-1 min-w-[8rem]">
          <div className="progress-track">
            <div className="progress-fill" style={{ width: `${percent}%` }} />
          </div>
        </div>
        <div className="flex gap-4 text-sm tabular-nums" style={{ color: "var(--parchment-dim)" }}>
          <span>
            <strong style={{ color: "var(--parchment)" }}>{Math.round(liveWpm)}</strong> wpm
          </span>
          <span>
            <strong style={{ color: "var(--parchment)" }}>{Math.round(liveAccuracy)}%</strong> accurate
          </span>
        </div>
      </div>

      {/* ── next-key hint ── */}
      {nextKey && (
        <p className="text-sm text-center" style={{ color: "var(--parchment-dim)" }}>
          {wrongAt ? "Not that one — press " : "Next: "}
          {nextKey.shift && (
            <>
              <kbd
                className="px-1.5 py-0.5 rounded text-xs"
                style={{ background: "var(--gold-bg)", color: "var(--gold-bright)" }}
              >
                Shift
              </kbd>
              {" + "}
            </>
          )}
          {isNamedKey(nextKey.char) ? (
            <kbd
              className="px-1.5 py-0.5 rounded text-xs"
              style={{ background: "var(--gold-bg)", color: "var(--gold-bright)" }}
            >
              {keyName(nextKey.char)}
            </kbd>
          ) : (
            <span className="font-arabic text-lg" style={{ color: "var(--gold-bright)" }}>
              {keyCapLabel(nextKey.char)}
            </span>
          )}
          <span> — {fingerLabel(layout?.keys?.[nextKey.code]?.finger)}</span>
        </p>
      )}

      <KeyboardDiagram layout={layout} nextKey={nextKey} wrongKey={wrongAt} />

      <div className="flex justify-between items-center">
        <button
          type="button"
          onClick={onExit}
          className="text-sm underline"
          style={{ color: "var(--parchment-dim)" }}
        >
          Back to lessons
        </button>
        <p className="text-xs" style={{ color: "var(--parchment-dim)" }}>
          Backspace steps back a letter.
        </p>
      </div>
    </div>
  );
}
